export interface ApiError {
  code: string
  message?: string
  fieldErrors?: Record<string, string[]>
}

export type HttpMethod = 'GET' | 'POST' | 'PUT' | 'PATCH' | 'DELETE'

interface RequestOptionsBase {
  method?: HttpMethod
  accessToken?: string
  credentials?: RequestCredentials
  headers?: HeadersInit
  skipSessionRefresh?: boolean
}

export type RequestOptions = RequestOptionsBase & (
  | { body?: unknown; formData?: never }
  | { body?: never; formData: FormData }
)

export interface BlobDownload {
  blob: Blob
  filename: string | null
}

export interface SessionRefreshResult {
  accessToken: string
}

export interface SessionRefreshHandler<TRefreshResult extends SessionRefreshResult = SessionRefreshResult> {
  refresh: (staleAccessToken: string) => Promise<TRefreshResult>
  onRefreshSuccess?: (result: TRefreshResult) => void
  onRefreshFailure?: (error: unknown) => void
}

let sessionRefreshHandler: SessionRefreshHandler | null = null
let inFlightSessionRefresh: { generation: number; promise: Promise<SessionRefreshResult> } | null = null
let lastSessionRefreshResult: { generation: number; result: SessionRefreshResult } | null = null
let sessionRefreshGeneration = 0

export function setSessionRefreshHandler<TRefreshResult extends SessionRefreshResult>(handler: SessionRefreshHandler<TRefreshResult> | null): void {
  sessionRefreshGeneration += 1
  sessionRefreshHandler = handler as SessionRefreshHandler | null
  lastSessionRefreshResult = null
  if (!handler || inFlightSessionRefresh?.generation !== sessionRefreshGeneration) {
    inFlightSessionRefresh = null
  }
}

export class ApiClientError extends Error {
  code: string
  status: number
  fieldErrors?: Record<string, string[]>

  constructor(status: number, apiError: ApiError) {
    super(apiError.message ?? apiError.code)
    this.name = 'ApiClientError'
    this.code = apiError.code
    this.status = status
    this.fieldErrors = apiError.fieldErrors
  }
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null
}

export function normalizeFieldErrors(value: unknown): Record<string, string[]> | undefined {
  if (!isRecord(value)) return undefined

  const normalized: Record<string, string[]> = {}
  Object.entries(value).forEach(([key, messages]) => {
    if (Array.isArray(messages)) {
      normalized[key] = messages.filter((message): message is string => typeof message === 'string')
      return
    }
    if (typeof messages === 'string') {
      normalized[key] = [messages]
    }
  })

  return Object.keys(normalized).length > 0 ? normalized : undefined
}

export function parseApiError(status: number, payload: unknown): ApiClientError {
  if (isRecord(payload) && typeof payload.code === 'string') {
    return new ApiClientError(status, {
      code: payload.code,
      message: typeof payload.message === 'string' ? payload.message : payload.code,
      fieldErrors: normalizeFieldErrors(payload.fieldErrors),
    })
  }

  return new ApiClientError(status, {
    code: status === 401 ? 'UNAUTHORIZED' : 'REQUEST_FAILED',
    message: 'REQUEST_FAILED',
  })
}

export async function readJson(response: Response): Promise<unknown> {
  const text = await response.text()
  if (!text) return undefined

  try {
    return JSON.parse(text) as unknown
  } catch {
    return undefined
  }
}

function requestInit(options: RequestOptions): RequestInit {
  const headers = new Headers(options.headers)
  if (options.body !== undefined && !headers.has('Content-Type')) {
    headers.set('Content-Type', 'application/json')
  }
  if (options.accessToken) {
    headers.set('Authorization', `Bearer ${options.accessToken}`)
  }

  return {
    method: options.method ?? 'GET',
    credentials: options.credentials ?? 'include',
    headers,
    body: options.formData ?? (options.body === undefined ? undefined : JSON.stringify(options.body)),
  }
}

function unquoteHeaderValue(value: string): string {
  const trimmed = value.trim()
  if (trimmed.startsWith('"') && trimmed.endsWith('"')) {
    return trimmed.slice(1, -1).replace(/\\"/g, '"')
  }
  return trimmed
}

function parseContentDispositionFilename(contentDisposition: string | null): string | null {
  if (!contentDisposition) return null

  const encodedMatch = /(?:^|;)\s*filename\*\s*=\s*([^;]+)/i.exec(contentDisposition)
  if (encodedMatch) {
    const value = unquoteHeaderValue(encodedMatch[1])
    const encodedFilename = value.split("'").length >= 3 ? value.split("'").slice(2).join("'") : value
    try {
      return decodeURIComponent(encodedFilename)
    } catch {
      return encodedFilename
    }
  }

  const plainMatch = /(?:^|;)\s*filename\s*=\s*("(?:\\"|[^"])*"|[^;]+)/i.exec(contentDisposition)
  if (!plainMatch) return null
  return unquoteHeaderValue(plainMatch[1])
}

function requestUrl(basePath: string, path: string): string {
  return `${basePath}${path}`
}

function isRefreshEndpoint(basePath: string, path: string): boolean {
  return requestUrl(basePath, path) === '/api/auth/refresh'
}

function canAttemptSessionRefresh(basePath: string, path: string, options: RequestOptions, status: number, hasRetried: boolean): options is RequestOptions & { accessToken: string } {
  return status === 401
    && Boolean(options.accessToken)
    && !hasRetried
    && options.skipSessionRefresh !== true
    && !isRefreshEndpoint(basePath, path)
    && sessionRefreshHandler !== null
}

async function refreshSession(staleAccessToken: string): Promise<{ generation: number; result: SessionRefreshResult }> {
  if (lastSessionRefreshResult && lastSessionRefreshResult.result.accessToken !== staleAccessToken) {
    return lastSessionRefreshResult
  }

  const handlerAtStart = sessionRefreshHandler
  const generationAtStart = sessionRefreshGeneration
  if (!handlerAtStart) {
    throw new Error('SESSION_REFRESH_UNAVAILABLE')
  }

  if (!inFlightSessionRefresh || inFlightSessionRefresh.generation !== generationAtStart) {
    const promise = handlerAtStart.refresh(staleAccessToken)
      .then(result => {
        if (sessionRefreshGeneration === generationAtStart && sessionRefreshHandler === handlerAtStart) {
          lastSessionRefreshResult = { generation: generationAtStart, result }
          handlerAtStart.onRefreshSuccess?.(result)
        }
        return result
      })
      .catch(error => {
        if (sessionRefreshGeneration === generationAtStart && sessionRefreshHandler === handlerAtStart) {
          lastSessionRefreshResult = null
          handlerAtStart.onRefreshFailure?.(error)
        }
        throw error
      })
      .finally(() => {
        if (inFlightSessionRefresh?.generation === generationAtStart) {
          inFlightSessionRefresh = null
        }
      })
    inFlightSessionRefresh = { generation: generationAtStart, promise }
  }

  const inFlight = inFlightSessionRefresh
  return {
    generation: inFlight.generation,
    result: await inFlight.promise,
  }
}

async function apiRequestInternal<T>(basePath: string, path: string, options: RequestOptions, hasRetried: boolean): Promise<T> {
  const response = await fetch(requestUrl(basePath, path), requestInit(options))

  const payload = await readJson(response)
  if (response.ok) {
    return payload as T
  }

  if (canAttemptSessionRefresh(basePath, path, options, response.status, hasRetried)) {
    let refreshedSession: SessionRefreshResult
    try {
      const refreshed = await refreshSession(options.accessToken)
      if (refreshed.generation !== sessionRefreshGeneration) {
        throw new Error('SESSION_REFRESH_INVALIDATED')
      }
      refreshedSession = refreshed.result
    } catch {
      throw parseApiError(response.status, payload)
    }
    return apiRequestInternal<T>(basePath, path, { ...options, accessToken: refreshedSession.accessToken }, true)
  }

  throw parseApiError(response.status, payload)
}

export async function apiRequest<T>(basePath: string, path: string, options: RequestOptions = {}): Promise<T> {
  return apiRequestInternal<T>(basePath, path, options, false)
}

async function apiBlobRequestInternal(basePath: string, path: string, options: RequestOptions, hasRetried: boolean): Promise<BlobDownload> {
  const response = await fetch(requestUrl(basePath, path), requestInit(options))

  if (response.ok) {
    return {
      blob: await response.blob(),
      filename: parseContentDispositionFilename(response.headers.get('Content-Disposition')),
    }
  }

  const payload = await readJson(response)
  if (canAttemptSessionRefresh(basePath, path, options, response.status, hasRetried)) {
    let refreshedSession: SessionRefreshResult
    try {
      const refreshed = await refreshSession(options.accessToken)
      if (refreshed.generation !== sessionRefreshGeneration) {
        throw new Error('SESSION_REFRESH_INVALIDATED')
      }
      refreshedSession = refreshed.result
    } catch {
      throw parseApiError(response.status, payload)
    }
    return apiBlobRequestInternal(basePath, path, { ...options, accessToken: refreshedSession.accessToken }, true)
  }

  throw parseApiError(response.status, payload)
}

export async function apiBlobRequest(basePath: string, path: string, options: RequestOptions = {}): Promise<BlobDownload> {
  return apiBlobRequestInternal(basePath, path, options, false)
}
